import { CommonModule, NgIfContext } from '@angular/common';
import { Directive, Input, NgModule, OnInit, TemplateRef, ViewContainerRef } from '@angular/core';
import { UserPermission } from '@elio/emanager-front/auth/model';
import { UserSelectors } from '@elio/emanager-front/auth/state';
import { Store } from '@ngrx/store';
import { take } from 'rxjs';

@Directive({
  selector: '[elioHasPermission], [elioHasEveryPermission], [elioHasPermissionIsSuperAdmin]',
})
export class HasPermissionDirective implements OnInit {
  /**
   *  Permission might be undefined because some components have optionals permissions.
   *  {@link ElioMenuButtonComponent} & {@link ElioListItemCardComponent}
   */
  @Input('elioHasPermission') permission: UserPermission | UserPermission[] | undefined = undefined;

  @Input('elioHasEveryPermission') permissions: UserPermission[] | undefined = undefined;

  /** isSuperAdmin is set if it has to check that the user is super admin. permission is then ignored. */
  @Input('elioHasPermissionIsSuperAdmin') isSuperAdmin = false;

  @Input('elioHasPermissionElseTemplate') elseTemplate?: TemplateRef<NgIfContext> | null;

  constructor(
    private templateRef: TemplateRef<unknown>,
    private viewContainer: ViewContainerRef,
    private store: Store
  ) {}

  ngOnInit(): void {
    if (this.isSuperAdmin) {
      this.store
        .select(UserSelectors.isSuperAdmin)
        .pipe(take(1))
        .subscribe((isSuperAdmin) => (isSuperAdmin ? this.addTemplate() : this.addElseTemplate()));
    } else if (this.permission) {
      this.store
        .select(UserSelectors.hasAnyPermission(this.permission))
        .pipe(take(1))
        .subscribe((hasPermission) => (hasPermission ? this.addTemplate() : this.addElseTemplate()));
    } else if (this.permissions) {
      this.store
        .select(UserSelectors.hasEveryPermission(this.permissions))
        .pipe(take(1))
        .subscribe((hasEveryPermission) => (hasEveryPermission ? this.addTemplate() : this.addElseTemplate()));
    } else {
      // elioHasPermission is sometimes used in ngFor with empty dynamic content
      this.addTemplate();
    }
  }

  private addTemplate() {
    this.viewContainer.createEmbeddedView(this.templateRef);
  }

  private addElseTemplate() {
    this.elseTemplate && this.viewContainer.createEmbeddedView(this.elseTemplate);
  }
}

@NgModule({
  imports: [CommonModule],
  declarations: [HasPermissionDirective],
  exports: [HasPermissionDirective],
})
export class ElioHasPermissionModule {}
